/**
 * AI CONTEXT
 * Purpose: global print queue drawer for evaluation batch print jobs.
 * Depends on: ApiContext print queue state; supports retry/remove/clear/export links.
 * Used by: App shell (always mounted).
 * Importance: async print workflow visibility while user multitasks.
 * Refactor: split row component/actions/status derivation into smaller hooks.
 */
import {
  CheckCircleOutlined,
  CloseOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { Button, Drawer, Space, Spin, Tooltip, Typography } from "antd"
import { noop } from "lodash"
import { AnimatePresence, motion, Reorder } from "motion/react"
import { forwardRef, useEffect, useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import TooltipBlock from "#components/views/AdminView/ClassScheduleTimeline/TooltipBlock"
import { useApiContext } from "#contexts/ApiContextProvider"
// All printing status and URL syncing is managed in ApiContextProvider
import { PrintEvalItem } from "#hooks/newHooks/usePrintEval"

const { Text } = Typography

type BatchPrintItemProps = {
  item: PrintEvalItem
  statusIcon: React.ReactNode
  url: string | undefined
  error: string | undefined
  onRetry: (itemId: string) => void
  onRemove: (itemId: string) => void
}

const BatchPrintItem = forwardRef<HTMLLIElement, BatchPrintItemProps>(
  ({ item, statusIcon, url, error, onRetry, onRemove }, ref) => {
    const [isAvailable, setIsAvailable] = useState(false)
    useEffect(() => {
      if (url) {
        setIsAvailable(true)
        setTimeout(() => {
          setIsAvailable(false)
        }, 1000 * 1000)
      }
    }, [url])

    const actions = useMemo(() => {
      return [
        url && isAvailable ? (
          <a
            style={{ padding: 4, fontSize: 10 }}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            key={`export-${item.id}`}
          >
            <ExportOutlined />
          </a>
        ) : url && !isAvailable ? (
          <Tooltip title="Expired" key={`export-expired-${item.id}`}>
            <ExportOutlined />
          </Tooltip>
        ) : null,
        error && (
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => onRetry(item.id)}
            key={`retry-${item.id}`}
          />
        ),
        <Button
          style={{ fontSize: 10 }}
          type="link"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => onRemove(item.id)}
          key={`remove-${item.id}`}
        />,
      ].filter(Boolean)
    }, [url, isAvailable, error, item.id, onRemove, onRetry])

    return (
      <Reorder.Item
        ref={ref}
        value={item}
        style={{ padding: 4, fontSize: 10, width: "100%" }}
      >
        <Flex
          style={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Flex gap={20}>
            <Flex>{statusIcon}</Flex>
            <Flex style={{ maxWidth: 120, minWidth: 0 }} grow>
              <TextOverrun text={item.studentName} />
            </Flex>
            <Flex>
              {error ? (
                <Text type="danger" style={{ fontSize: 10 }}>
                  {error}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {item.evaluation.currentSkill} → {item.evaluation.nextSkill}
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex>{actions}</Flex>
        </Flex>
      </Reorder.Item>
    )
  }
)

// Global component
const BatchPrintDrawer = () => {
  const [celebrationKey, setCelebrationKey] = useState(0)
  const {
    isBatchModalOpen: isOpen,
    closeBatchModal: onClose,
    printItems: printItems,
    retryItem: onRetry,
    removeItem: onRemove,
    clearFinished: onClearFinished,
  } = useApiContext()

  const getStatusIcon = (status: PrintEvalItem["status"]) => {
    switch (status) {
      case "printed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />
      case "error":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
      case "processing":
        return <Spin size="small" />
      case "pending":
      default:
        return <EllipsisOutlined style={{ color: "#1890ff" }} />
    }
  }

  const { statusesById, errorsById, urlsById } = useMemo(() => {
    const statusMap: Record<
      string,
      "printed" | "error" | "processing" | "pending"
    > = {}
    const errorMap: Record<string, string | undefined> = {}
    const urlMap: Record<string, string | undefined> = {}
    printItems?.forEach((item) => {
      statusMap[item.evaluation.id] = item.status || "pending"
      errorMap[item.evaluation.id] = item.error || undefined
      urlMap[item.evaluation.id] = item.url || undefined
    })
    return {
      statusesById: statusMap,
      errorsById: errorMap,
      urlsById: urlMap,
    }
  }, [printItems])

  const getEffectiveStatus = (
    serverStatus: "printed" | "error" | "processing" | "pending" | undefined
  ): PrintEvalItem["status"] => {
    if (serverStatus === "printed") return "printed"
    if (serverStatus === "error") return "error"
    if (serverStatus === "processing") return "processing"
    if (serverStatus === "pending") return "pending"
    // When opening/re-opening modal, default all items to pending until server emits
    return "pending"
  }

  // Calculate overall statuses
  const statuses = printItems.map((item) =>
    getEffectiveStatus(statusesById?.[item.evaluation.id])
  )
  const counts = statuses.reduce(
    (acc, s) => {
      acc[s] = (acc[s] || 0) + 1
      return acc
    },
    { printed: 0, error: 0, processing: 0, pending: 0 } as Record<
      "printed" | "error" | "processing" | "pending",
      number
    >
  )
  const overallStatuses = {
    allDone: statuses.every((s) => s === "printed"),
    hasErrors: statuses.some((s) => s === "error"),
    isProcessing: statuses.some((s) => s === "processing"),
    counts,
  }

  // Auto-download has been moved to ApiContextProvider

  // Deprecated local-only derived flags (kept here for reference, but unused):
  // const allDone = printItems.every((item) => item.status === "done")
  // const hasErrors = printItems.some((item) => item.status === "error")
  // const isProcessing = printItems.some((item) => item.status === "processing")

  // Sort items: error (top), pending/processing (middle), done (bottom)
  const sortedItems = useMemo(() => {
    const order: Record<PrintEvalItem["status"], number> = {
      error: 0,
      processing: 1,
      pending: 2,
      printed: 3,
    }
    return [...printItems].sort((a, b) => {
      const aStatus = getEffectiveStatus(statusesById?.[a.evaluation.id])
      const bStatus = getEffectiveStatus(statusesById?.[b.evaluation.id])
      return order[aStatus] - order[bStatus]
    })
  }, [printItems, statusesById])

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title="Print Queue"
      width={300}
      mask={false}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Text type="secondary">
              <TooltipBlock title="Downloads">
                <div>Pending: {overallStatuses.counts.pending}</div>
                <div>Processing: {overallStatuses.counts.processing}</div>
                <div>Done: {overallStatuses.counts.printed}</div>
                <div>Failed: {overallStatuses.counts.error}</div>
              </TooltipBlock>
            </Text>
          </Space>
          <Space>
            <Button
              onClick={() => {
                onClearFinished()
                setCelebrationKey((k) => k + 1)
              }}
            >
              Clear finished
            </Button>
          </Space>
        </Space>
      }
    >
      {sortedItems.length > 0 ? (
        <Reorder.Group
          axis="y"
          onReorder={noop} // Keep items in original order
          layoutScroll
          values={sortedItems}
          style={{ paddingBottom: 150 }}
        >
          <AnimatePresence mode="popLayout">
            {sortedItems.map((item) => {
              const status = getEffectiveStatus(
                statusesById?.[item.evaluation.id]
              )
              const statusIcon = getStatusIcon(status)
              const url = urlsById?.[item.evaluation.id]
              const error = errorsById?.[item.evaluation.id] || item.error

              return (
                <BatchPrintItem
                  key={item.evaluation.id}
                  item={item}
                  statusIcon={statusIcon}
                  url={url}
                  error={error}
                  onRetry={onRetry}
                  onRemove={onRemove}
                />
              )
            })}
          </AnimatePresence>
        </Reorder.Group>
      ) : (
        <Flex col center style={{ padding: 20 }}>
          <motion.div key={`celebrate-${celebrationKey}`}>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 140,
              }}
            >
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle
                  cx="40"
                  cy="40"
                  r="38"
                  fill="#e6f9ec"
                  stroke="#27ae60"
                  strokeWidth="4"
                />
                <motion.path
                  d="M25 42 L37 54 L56 32"
                  fill="none"
                  stroke="#27ae60"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.5 }}
                />
              </svg>
            </motion.div>
            <AnimateInFlex
              key={`celebrate-${celebrationKey}`}
              animation={ANIMATION_BY_KEY.down}
            >
              <Text type="secondary" style={{ marginTop: 12, fontSize: 18 }}>
                All clear!
              </Text>
            </AnimateInFlex>
          </motion.div>
        </Flex>
      )}
    </Drawer>
  )
}

export default BatchPrintDrawer
